
import React, { useState, useCallback } from "react";
import { GoogleMap, LoadScript, Marker, Polyline, InfoWindow, StandaloneSearchBox } from "@react-google-maps/api";
import { C, RC, RF, apiFetch } from "../utils/api";

const LIBS = ["places"];

const FIBRA_COLORS = {
  "96": "#EF4444", "48": "#F97316", "24": "#1E7BBF",
  "12": "#10B981", "8": "#06B6D4", "6": "#8B5CF6",
};

const SVG_MUFA = `data:image/svg+xml,${encodeURIComponent("<svg xmlns=\"http://www.w3.org/2000/svg\" width=\"20\" height=\"20\" viewBox=\"0 0 20 20\"><circle cx=\"10\" cy=\"10\" r=\"9.5\" fill=\"#FBBF24\" stroke=\"#92400E\" stroke-width=\"0.8\"/><circle cx=\"10\" cy=\"10\" r=\"2.5\" fill=\"#111\"/><path d=\"M10,7.5 L10,1 A2.5,2.5 0 0,1 12.2,2.3 Z\" fill=\"#111\"/><path d=\"M10,7.5 L10,1 A2.5,2.5 0 0,0 7.8,2.3 Z\" fill=\"#111\"/><path d=\"M10,12.5 L10,19 A2.5,2.5 0 0,1 7.8,17.7 Z\" fill=\"#111\"/><path d=\"M10,12.5 L10,19 A2.5,2.5 0 0,0 12.2,17.7 Z\" fill=\"#111\"/><path d=\"M12.5,10 L19,10 A2.5,2.5 0 0,1 17.7,12.2 Z\" fill=\"#111\"/><path d=\"M12.5,10 L19,10 A2.5,2.5 0 0,0 17.7,7.8 Z\" fill=\"#111\"/><path d=\"M7.5,10 L1,10 A2.5,2.5 0 0,1 2.3,7.8 Z\" fill=\"#111\"/><path d=\"M7.5,10 L1,10 A2.5,2.5 0 0,0 2.3,12.2 Z\" fill=\"#111\"/><circle cx=\"10\" cy=\"10\" r=\"1.2\" fill=\"#FBBF24\"/></svg>")}`;
const SVG_NAP8 = `data:image/svg+xml,${encodeURIComponent("<svg xmlns=\"http://www.w3.org/2000/svg\" width=\"20\" height=\"18\" viewBox=\"0 0 20 18\"><polygon points=\"10,17 1,1 19,1\" fill=\"#16A34A\" stroke=\"#14532D\" stroke-width=\"1\" stroke-linejoin=\"round\"/></svg>")}`;
const SVG_NAP16 = `data:image/svg+xml,${encodeURIComponent("<svg xmlns=\"http://www.w3.org/2000/svg\" width=\"20\" height=\"18\" viewBox=\"0 0 20 18\"><polygon points=\"10,17 1,1 19,1\" fill=\"#2563EB\" stroke=\"#1E3A8A\" stroke-width=\"1\" stroke-linejoin=\"round\"/></svg>")}`;
const SVG_RESERVA = `data:image/svg+xml,${encodeURIComponent("<svg xmlns=\"http://www.w3.org/2000/svg\" width=\"20\" height=\"20\" viewBox=\"0 0 20 20\"><circle cx=\"10\" cy=\"10\" r=\"9.5\" fill=\"#1F2937\" stroke=\"#111\" stroke-width=\"0.8\"/><circle cx=\"6.5\" cy=\"8\" r=\"3\" fill=\"none\" stroke=\"white\" stroke-width=\"1\"/><circle cx=\"13.5\" cy=\"8\" r=\"3\" fill=\"none\" stroke=\"white\" stroke-width=\"1\"/><circle cx=\"10\" cy=\"13\" r=\"3\" fill=\"none\" stroke=\"white\" stroke-width=\"1\"/></svg>")}`;
const SVG_OLT = `data:image/svg+xml,${encodeURIComponent('<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24"><circle cx="12" cy="12" r="11" fill="#DC2626" stroke="#7F1D1D" stroke-width="1.5"/><line x1="12" y1="4" x2="12" y2="20" stroke="white" stroke-width="1.5"/><line x1="4" y1="12" x2="20" y2="12" stroke="white" stroke-width="1.5"/><line x1="6.3" y1="6.3" x2="17.7" y2="17.7" stroke="white" stroke-width="1.5"/><line x1="17.7" y1="6.3" x2="6.3" y2="17.7" stroke="white" stroke-width="1.5"/></svg>')}`;
const ICONS = {
  olt: "https://maps.google.com/mapfiles/kml/pal3/icon47.png",
  nap_activo: "https://maps.google.com/mapfiles/kml/paddle/grn-square.png",
  nap_danado: "https://maps.google.com/mapfiles/ms/icons/red-dot.png",
  nap_mantenimiento: "https://maps.google.com/mapfiles/ms/icons/orange-dot.png",
  mufa_activo: "https://maps.google.com/mapfiles/kml/paddle/pink-diamond.png",
  mufa_danado: "https://maps.google.com/mapfiles/ms/icons/red-dot.png",
};

function haversine(p1, p2) {
  const R = 6371000;
  const dLat = (p2.lat - p1.lat) * Math.PI / 180;
  const dLng = (p2.lng - p1.lng) * Math.PI / 180;
  const a = Math.sin(dLat/2)**2 + Math.cos(p1.lat*Math.PI/180) * Math.cos(p2.lat*Math.PI/180) * Math.sin(dLng/2)**2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
}

function calcDistancia(points) {
  let d = 0;
  for(let i=1; i<points.length; i++) d += haversine(points[i-1], points[i]);
  return Math.round(d);
}

function getEquiposEnRuta(points, equipos, threshold=0.0003) {
  const all = [
    ...(equipos.olts||[]).map(e=>({...e,tipo:"olt"})),
    ...(equipos.mufas||[]).map(e=>({...e,tipo:"mufa"})),
    ...(equipos.naps||[]).map(e=>({...e,tipo:"nap"})),
  ];
  const encontrados = [];
  for(const pt of points){
    const eq = all.find(e=>Math.abs(parseFloat(e.lat)-pt.lat)<threshold && Math.abs(parseFloat(e.lng)-pt.lng)<threshold);
    if(eq && !encontrados.find(x=>x.id===eq.id && x.tipo===eq.tipo)) encontrados.push(eq);
  }
  return encontrados;
}

function calcReservas(points, equipos) {
  const eqs = getEquiposEnRuta(points, equipos);
  let reserva = 10; // inicio
  for(let i=1; i<eqs.length-1; i++) reserva += 20; // intermedios
  if(eqs.length>1) reserva += 20; // ultimo dispositivo o punta
  else reserva += 10; // punta libre
  return reserva;
}

function getEquipoAt(lat, lng, equipos, threshold=0.0003) {
  const all = [
    ...(equipos.olts||[]).map(e=>({...e,tipo:"olt"})),
    ...(equipos.mufas||[]).map(e=>({...e,tipo:"mufa"})),
    ...(equipos.naps||[]).map(e=>({...e,tipo:"nap"})),
  ];
  return all.find(e=>Math.abs(parseFloat(e.lat)-lat)<threshold && Math.abs(parseFloat(e.lng)-lng)<threshold) || null;
}

// Load Material Symbols font
if(!document.getElementById("material-symbols-font")){
  const l=document.createElement("link");
  l.id="material-symbols-font";
  l.rel="stylesheet";
  l.href="https://fonts.googleapis.com/css2?family=Material+Symbols+Outlined:opsz,wght,FILL,GRAD@20..48,100..700,0..1,-50..200";
  document.head.appendChild(l);
}

export default function Mapa({equipos, cfg, addMode, setAddMode, setForm, sel, setSel, mapCenter, setMapCenter, mapZoom, setMapZoom, user, loadCl, clientes, delEq, fibraSelId, setFibraSelId, onVerDiagrama, onVerDiagramaMufa, onVerDiagramaOlt, onLoadEq}) {
  const [searchBox, setSearchBox] = useState(null);
  const mapRef = React.useRef(null);

  React.useEffect(()=>{
    if(mapRef.current){
      mapRef.current.panTo(mapCenter);
      mapRef.current.setZoom(mapZoom);
    }
  },[mapCenter, mapZoom]);
  const [fibraPoints, setFibraPoints] = useState([]);
  const [fibraOrigen, setFibraOrigen] = useState(null);
  const [fibraInfo, setFibraInfo] = useState(null);
  const [fibraPanel, setFibraPanel] = useState(null);
  const [modoReserva, setModoReserva] = useState(null);
  const [capa, setCapa] = useState(2); // 1=Postes, 2=Dispositivos, 3=Red optica // fibra seleccionada para agregar reserva // {points, eqs, origen}
  const [fibraNombre, setFibraNombre] = useState("");
  const [fibraHilos, setFibraHilos] = useState("12");
  const [fibraBuffers, setFibraBuffers] = useState("1");
  const [editingFibra, setEditingFibra] = useState(null);
  const [editingFibraId, setEditingFibraId] = useState(null);
  const [fibraVersion, setFibraVersion] = useState(0);
  const modoFibra = addMode === "fibra";

  const onPlacesChanged = ()=>{
    const places = searchBox.getPlaces();
    if(places&&places.length>0){
      const loc = places[0].geometry.location;
      setMapCenter({lat:loc.lat(), lng:loc.lng()});
      setMapZoom(16);
    }
  };

  const onMapClick = useCallback((e)=>{
    const lat = e.latLng.lat();
    const lng = e.latLng.lng();
    if(modoReserva){
      // Guardar reserva en este punto
      const metros = prompt("Metros de reserva:");
      if(metros){
        apiFetch("/equipos.php?tipo=reserva",{method:"POST",headers:{"Content-Type":"application/json"},
          body:JSON.stringify({lat,lng,metraje:parseInt(metros)||0,nombre:"Reserva",fibra_id:modoReserva.id,notas:""})
        }).then(()=>{ setModoReserva(null); });
      } else {
        setModoReserva(null);
      }
      return;
    }
    if(modoFibra){
      setFibraPoints(pts=>[...pts, {lat, lng}]);
      return;
    }
    if(!addMode) return;
    setForm({tipo:addMode, lat, lng});
    setAddMode(null);
  },[addMode, modoFibra, setAddMode, setForm]);

  const onMarkerClick = useCallback((equipo)=>{
    if(modoFibra){
      const pt = {lat:parseFloat(equipo.lat), lng:parseFloat(equipo.lng)};
      setFibraPoints(pts=>{
        if(pts.length===0) setFibraOrigen({tipo:equipo.tipo, id:equipo.id});
        return [...pts, pt];
      });
      return;
    }
    setSel(equipo);
    if(equipo.tipo==="nap") loadCl(equipo.id);
  },[modoFibra, setSel, loadCl]);

  const onDragDispositivo = async(tipo, d, lat, lng)=>{
    await apiFetch("/equipos.php?tipo="+tipo,{method:"PUT",headers:{"Content-Type":"application/json"},body:JSON.stringify({...d,lat,lng})});
    if(onLoadEq) onLoadEq();
  };

  const onDragPoste = async(p, lat, lng)=>{
    await apiFetch("/equipos.php?tipo=poste",{method:"PUT",headers:{"Content-Type":"application/json"},
      body:JSON.stringify({id:p.id,nombre:p.nombre,lat,lng,configurado:p.configurado,
        numeracion:p.numeracion,material:p.material,tipo_energia:p.tipo_energia,
        altura_metros:p.altura_metros,tiene_luz:p.tiene_luz,tiene_transformador:p.tiene_transformador,
        retenciones:p.retenciones,cantidad_cables:p.cantidad_cables,cantidad_dispositivos:p.cantidad_dispositivos,notas:p.notas})});
    // Mover dispositivos asociados
    const disp = getDispositivosPoste(equipos,p);
    for(const m of disp.mufas) await apiFetch("/equipos.php?tipo=mufa",{method:"PUT",headers:{"Content-Type":"application/json"},body:JSON.stringify({...m,lat,lng})});
    for(const n of disp.naps) await apiFetch("/equipos.php?tipo=nap",{method:"PUT",headers:{"Content-Type":"application/json"},body:JSON.stringify({...n,lat,lng})});
    if(onLoadEq) onLoadEq();
  };

  const polylineRefs = React.useRef({});

  const saveEditableFibra = async(fibraId, polylineRef)=>{
    if(!polylineRef) return;
    const path = polylineRef.getPath();
    const coords = [];
    for(let i=0;i<path.getLength();i++){
      const pt = path.getAt(i);
      coords.push({lat:pt.lat(),lng:pt.lng()});
    }
    await apiFetch("/equipos.php?tipo=fibra",{method:"PUT",headers:{"Content-Type":"application/json"},
      body:JSON.stringify({id:fibraId,coordenadas:JSON.stringify(coords),
        nombre:editingFibra?.nombre||"",tipo_hilos:editingFibra?.tipo_hilos||12,
        tipo_instalacion:editingFibra?.tipo_instalacion||"aerea",
        estado:editingFibra?.estado||"activo",notas:editingFibra?.notas||""})});
    if(polylineRef) polylineRef.setOptions({editable:false, strokeOpacity:0});
    setEditingFibraId(null); setFibraSelId(null); setFibraVersion(v=>v+1); if(onLoadEq) onLoadEq();
    setEditingFibra(null);
    setAddMode(null);
    if(onLoadEq) onLoadEq();
  };

  const finishFibra = ()=>{
    if(fibraPoints.length < 2){ alert("Dibuja al menos 2 puntos"); return; }
    const eqs = getEquiposEnRuta(fibraPoints, equipos);
    setFibraPanel({points:[...fibraPoints], eqs, origen:fibraOrigen, editing:editingFibra});
    setFibraNombre(editingFibra?.nombre||"");
    setFibraHilos(editingFibra?.tipo_hilos||"12");
  };

  const confirmarFibra = async()=>{
    if(!fibraNombre){ alert("Escribe un nombre para la fibra"); return; }
    const {points, eqs, origen, editing} = fibraPanel;
    const lastPt = points[points.length-1];
    const destino = getEquipoAt(lastPt.lat, lastPt.lng, equipos);
    const coords  = JSON.stringify(points);

    // Guardar/actualizar fibra
    let fibraId;
    if(editing){
      await apiFetch("/equipos.php?tipo=fibra",{method:"PUT",headers:{"Content-Type":"application/json"},
        body:JSON.stringify({id:editing.id,nombre:fibraNombre,tipo_hilos:parseInt(fibraHilos),num_buffers:parseInt(fibraBuffers),hilos_por_buffer:Math.ceil(parseInt(fibraHilos)/parseInt(fibraBuffers)),
          coordenadas:coords,
          origen_tipo:origen?.tipo||"olt", origen_id:parseInt(origen?.id)||0,
          destino_tipo:destino?.tipo||"nap", destino_id:parseInt(destino?.id)||0,
          tipo_instalacion:"aerea", estado:"activo"})});
      fibraId = editing.id;
    } else {
      const r = await apiFetch("/equipos.php?tipo=fibra",{method:"POST",headers:{"Content-Type":"application/json"},
        body:JSON.stringify({nombre:fibraNombre,tipo_hilos:parseInt(fibraHilos),num_buffers:parseInt(fibraBuffers),hilos_por_buffer:Math.ceil(parseInt(fibraHilos)/parseInt(fibraBuffers)),
          coordenadas:coords,
          origen_tipo:origen?.tipo||"olt", origen_id:parseInt(origen?.id)||0,
          destino_tipo:destino?.tipo||"nap", destino_id:parseInt(destino?.id)||0,
          tipo_instalacion:"aerea", estado:"activo", proyecto_id:1})});
      fibraId = r.id;
    }

    // Guardar recorrido en fibra_recorrido
    if(fibraId && eqs.length>0){
      await apiFetch("/fibra_recorrido.php",{method:"POST",headers:{"Content-Type":"application/json"},
        body:JSON.stringify({accion:"guardar_recorrido", fibra_id:fibraId,
          dispositivos: eqs.map((eq,i)=>({
            dispositivo_tipo: eq.tipo,
            dispositivo_id:   eq.id,
            orden:            i+1,
            tipo_punto:       i===0?"inicio":i===eqs.length-1?"terminacion":"paso",
          }))
        })});
    }

    // Limpiar y recargar
    setFibraPanel(null); setFibraPoints([]); setFibraOrigen(null);
    setEditingFibra(null); setAddMode(null);
    if(onLoadEq) onLoadEq();
  };

  const cancelFibra = ()=>{ setFibraPoints([]); setFibraOrigen(null); setEditingFibra(null); setAddMode(null); };
  const undoPoint = ()=>setFibraPoints(pts=>pts.slice(0,-1));

  const onFibraClick = (fibra)=>{
    setFibraSelId(fibra.id);
    if(modoFibra) return;
    try {
      const coords = fibra.coordenadas ? JSON.parse(fibra.coordenadas) : [];
      const eqs = getEquiposEnRuta(coords, equipos);
      const distReal = calcDistancia(coords);
      const reserva = calcReservas(coords, equipos);
      setFibraInfo({fibra, coords, eqs, distReal, distTotal: distReal + reserva, reserva});
    } catch(e){}
  };

  const editarFibra = (info)=>{
    try {
      const coords = info.fibra.coordenadas ? JSON.parse(info.fibra.coordenadas) : [];
      setFibraPoints(coords);
      setFibraOrigen(info.eqs.length>0 ? {tipo:info.eqs[0].tipo, id:info.eqs[0].id} : null);
      setEditingFibra(info.fibra);
      setEditingFibraId(info.fibra.id);
      setFibraSelId(info.fibra.id);
      setFibraInfo(null);
      setAddMode("fibra");
    } catch(e){}
  };

  const lines = (equipos.fibras||[]).map(f=>{
    try {
      const coords = f.coordenadas ? JSON.parse(f.coordenadas) : null;
      const color = f.estado==="danado" ? "#EF4444" : (FIBRA_COLORS[f.tipo_hilos] || "#1E7BBF");
      if(coords&&coords.length>1) return {id:f.id, path:coords, color, fibra:f};
      const all=[...(equipos.olts||[]),...(equipos.mufas||[]),...(equipos.naps||[])];
      const o=all.find(x=>x.id==f.origen_id);
      const d=all.find(x=>x.id==f.destino_id);
      if(!o||!d) return null;
      return {id:f.id, path:[{lat:parseFloat(o.lat),lng:parseFloat(o.lng)},{lat:parseFloat(d.lat),lng:parseFloat(d.lng)}], color, fibra:f};
    } catch(e){ return null; }
  }).filter(Boolean);

  // Genera simbolos SVG de dispositivos encima del poste
  const getDispositivosPoste=(equipos, poste)=>{
    const mufas = (equipos.mufas||[]).filter(m=>String(m.poste_id)===String(poste.id));
    const naps  = (equipos.naps||[]).filter(n=>String(n.poste_id)===String(poste.id));
    return {mufas, naps, total: mufas.length + naps.length};
  };

  const svgMufa=(x,y,s=10)=>`
    <circle cx="${x}" cy="${y}" r="${s/2}" fill="#FCD34D" stroke="#111" stroke-width="0.6"/>
    <path d="M${x},${y} L${x},${y-s/2+1} A${s/2-1},${s/2-1} 0 0,1 ${x+(s/2-1)*0.866},${y-(s/2-1)*0.5} Z" fill="#111"/>
    <path d="M${x},${y} L${x+(s/2-1)*0.866},${y+(s/2-1)*0.5} A${s/2-1},${s/2-1} 0 0,1 ${x-(s/2-1)*0.866},${y+(s/2-1)*0.5} Z" fill="#111"/>
    <path d="M${x},${y} L${x-(s/2-1)*0.866},${y-(s/2-1)*0.5} A${s/2-1},${s/2-1} 0 0,1 ${x},${y-s/2+1} Z" fill="#111"/>
    <circle cx="${x}" cy="${y}" r="${s/5}" fill="#FCD34D"/>
    <circle cx="${x}" cy="${y}" r="${s/8}" fill="#111"/>`;

  const svgNap=(x,y,puertos,s=12)=>{
    const col = parseInt(puertos)>=16?"#2563EB":"#16A34A";
    const brd = parseInt(puertos)>=16?"#1E3A8A":"#14532D";
    const h = s*0.9;
    return `<polygon points="${x},${y+h/2} ${x+s/2},${y-h/2} ${x-s/2},${y-h/2}" fill="${col}" stroke="${brd}" stroke-width="0.8" stroke-linejoin="round"/>`;
  };

  const svgReserva=(x,y,s=10)=>`
    <circle cx="${x}" cy="${y}" r="${s/2}" fill="none" stroke="#111" stroke-width="0.8"/>
    <circle cx="${x-s/4}" cy="${y-s/6}" r="${s/4}" fill="none" stroke="#111" stroke-width="0.6"/>
    <circle cx="${x+s/4}" cy="${y-s/6}" r="${s/4}" fill="none" stroke="#111" stroke-width="0.6"/>
    <circle cx="${x}" cy="${y+s/4}" r="${s/4}" fill="none" stroke="#111" stroke-width="0.6"/>`;

  const buildDispositivosSvg=(equipos, poste)=>{
    const {mufas,naps,total} = getDispositivosPoste(equipos,poste);
    if(total===0) return null;
    const items = [...mufas.map(m=>({tipo:"mufa",puertos:0})), ...naps.map(n=>({tipo:"nap",puertos:n.puertos}))];
    const s = total===1?14:total===2?12:10;
    const totalW = total*s+(total-1)*2;
    const startX = 12 - totalW/2 + s/2;
    let svg = '';
    items.forEach((it,i)=>{
      const x = startX + i*(s+2);
      const y = 6;
      if(it.tipo==="mufa") svg += svgMufa(x,y,s);
      else svg += svgNap(x,y,it.puertos,s);
    });
    return svg;
  };

  const getIconPoste=(p, sinDispositivos=false)=>{
    // Sin configuracion → icono default con ?
    if(!parseInt(p.configurado||0)){
      const dispSvg = sinDispositivos ? null : buildDispositivosSvg(equipos, p);
      if(dispSvg){
        const svgStr = `<svg xmlns="http://www.w3.org/2000/svg" width="24" height="48" viewBox="0 0 24 48">${dispSvg}<rect x="11" y="14" width="2" height="28" fill="#888"/><rect x="10" y="41" width="4" height="2" fill="#666"/></svg>`;
        return `data:image/svg+xml,${encodeURIComponent(svgStr)}`;
      }
      return `/img/postes/poste_default.svg`;
    }
    const m = p.material==="metalico" ? 1 : 0;
    const e = p.tipo_energia||"sin_energia";
    const l = parseInt(p.tiene_luz)||0;
    const t = parseInt(p.tiene_transformador)||0;
    // Sin energia
    if(e==="sin_energia") return `/img/postes/0_poste_sin_energia${m?"_metal":""}.svg`;
    let num;
    if(!m){
      if(e==="baja_tension")         num = l?(t?4:2):(t?3:1);
      else if(e==="media_tension")   num = l?(t?8:6):(t?7:5);
      else                           num = l?(t?12:10):(t?11:9);
    } else {
      if(e==="baja_tension")         num = l?(t?16:14):(t?15:13);
      else if(e==="media_tension")   num = l?(t?20:18):(t?19:17);
      else                           num = l?(t?24:22):(t?23:21);
    }
    // Construir nombre archivo correcto
    const base = e==="baja_tension"?"baja":e==="media_tension"?"media":"media_baja";
    const dispSvg2 = sinDispositivos ? null : buildDispositivosSvg(equipos, p);
    if(dispSvg2){
      const posteColor = m ? "#DC2626" : "#111";
      const hasMedia = e==="media_tension"||e==="media_baja_tension";
      const hasBaja  = e==="baja_tension"||e==="media_baja_tension";
      const mediaY = 18; const bajaY = hasMedia ? 26 : 18;
      const posteSvg = `<svg xmlns="http://www.w3.org/2000/svg" width="24" height="52" viewBox="0 0 24 52">
        ${dispSvg2}
        <rect x="11" y="14" width="2" height="34" fill="${posteColor}"/>
        ${hasMedia?`<rect x="2" y="${mediaY}" width="18" height="2" fill="#111"/>
        <line x1="12" y1="${mediaY+2}" x2="7" y2="${mediaY}" stroke="#111" stroke-width="1.5"/>
        <line x1="12" y1="${mediaY+2}" x2="17" y2="${mediaY}" stroke="#111" stroke-width="1.5"/>
        <rect x="1" y="${mediaY-2}" width="3" height="3" rx="0.5" fill="#CC0000"/>
        <rect x="5" y="${mediaY-2}" width="3" height="3" rx="0.5" fill="#CC0000"/>
        <rect x="15" y="${mediaY-2}" width="3" height="3" rx="0.5" fill="#CC0000"/>
        <rect x="19" y="${mediaY-2}" width="3" height="3" rx="0.5" fill="#CC0000"/>`:""}
        ${hasBaja?`<rect x="4" y="${bajaY}" width="14" height="2" fill="#444" stroke="#22CC00" stroke-width="0.5"/>
        <rect x="3" y="${bajaY-2}" width="3" height="3" rx="0.5" fill="#CC0000"/>
        <rect x="7" y="${bajaY-2}" width="3" height="3" rx="0.5" fill="#CC0000"/>
        <rect x="13" y="${bajaY-2}" width="3" height="3" rx="0.5" fill="#CC0000"/>
        <rect x="17" y="${bajaY-2}" width="3" height="3" rx="0.5" fill="#CC0000"/>`:""}
        ${t?`<rect x="4" y="32" width="12" height="8" rx="1" fill="#333" stroke="#22CC00" stroke-width="0.8"/><rect x="6" y="34" width="8" height="4" rx="0.5" fill="#10B981"/>`:""}
        ${l?`<line x1="13" y1="40" x2="17" y2="40" stroke="#111" stroke-width="1.5"/><rect x="16" y="37" width="6" height="4" rx="2" fill="#111"/>`:""}
        <rect x="10" y="47" width="4" height="2" fill="#444"/>
      </svg>`;
      return `data:image/svg+xml,${encodeURIComponent(posteSvg)}`;
    }
    return `/img/postes/${num}_poste_${base}${t?"_trafo":""}${l?"_luz":""}${m?"_metal":""}.svg`;
  };

  const getIcon=(tipo,estado,puertos)=>{
    if(tipo==="olt") return SVG_OLT;
    if(tipo==="mufa") return SVG_MUFA;
    if(tipo==="nap") return parseInt(puertos)>=16 ? SVG_NAP16 : SVG_NAP8;
    return SVG_NAP8;
  };

  const tipoLabel = t => t==="olt"?"OLT":t==="mufa"?"Mufa":"NAP";

  return (
    <div style={{height:"100%",position:"relative"}}>
      {/* SELECTOR DE CAPAS */}
      <div style={{position:"absolute",bottom:8,left:"50%",transform:"translateX(-50%)",zIndex:100,display:"flex",gap:2,background:"rgba(0,0,0,0.7)",borderRadius:20,padding:"4px"}}>
        {[[1,"🏗️ Postes"],[2,"📦 Dispositivos"],[3,"🌐 Red óptica"]].map(([c,l])=>(
          <button key={c} onClick={()=>setCapa(c)} style={{
            padding:"5px 10px",borderRadius:16,border:"none",fontSize:11,cursor:"pointer",fontWeight:600,
            background:capa===c?"#7C3AED":"transparent",
            color:capa===c?"#fff":"#9CA3AF"
          }}>{l}</button>
        ))}
      </div>

      {editingFibraId&&(
        <div style={{position:"absolute",top:8,left:"50%",transform:"translateX(-50%)",zIndex:100,display:"flex",gap:6,background:"rgba(0,0,0,0.8)",borderRadius:8,padding:"6px 10px",alignItems:"center"}}>
          <span style={{color:"#00FF00",fontSize:11}}>✎ Editando fibra — arrastra los puntos</span>
          <button onClick={()=>saveEditableFibra(editingFibraId, polylineRefs.current[editingFibraId])}
            style={{background:"#10B981",color:"#fff",border:"none",borderRadius:6,padding:"4px 12px",fontSize:11,cursor:"pointer",fontWeight:600}}>
            ✓ Guardar
          </button>
          <button onClick={()=>{setEditingFibraId(null); setFibraSelId(null); setFibraVersion(v=>v+1); setTimeout(()=>{ if(onLoadEq) onLoadEq(); },300);setEditingFibra(null);setAddMode(null);}}
            style={{background:"rgba(239,68,68,0.3)",color:"#F87171",border:"1px solid #F87171",borderRadius:6,padding:"4px 10px",fontSize:11,cursor:"pointer"}}>
            Cancelar
          </button>
        </div>
      )}

      {modoReserva && (
        <div style={{position:"absolute",top:0,left:0,right:0,background:"rgba(202,138,4,0.9)",color:"#fff",padding:"8px 16px",zIndex:100,display:"flex",alignItems:"center",justifyContent:"space-between"}}>
          <span style={{fontSize:12,fontWeight:600}}>📌 Toca el mapa para colocar la reserva sobre la fibra {modoReserva.nombre}</span>
          <button onClick={()=>setModoReserva(null)} style={{background:"rgba(0,0,0,0.3)",border:"none",color:"#fff",borderRadius:6,padding:"3px 10px",cursor:"pointer",fontSize:11}}>Cancelar</button>
        </div>
      )}
      {addMode && addMode!=="fibra" && (
        <div style={{position:"absolute",top:10,left:"50%",transform:"translateX(-50%)",background:"#F59E0B",color:"#000",padding:"8px 16px",borderRadius:20,fontSize:13,fontWeight:600,zIndex:20}}>
          Toca el mapa para agregar {addMode.toUpperCase()}
          <span onClick={()=>setAddMode(null)} style={{marginLeft:10,cursor:"pointer"}}>X</span>
        </div>
      )}
      {modoFibra && (
        <div style={{position:"absolute",top:10,left:"50%",transform:"translateX(-50%)",background:"#1E7BBF",color:"#fff",padding:"8px 12px",borderRadius:20,fontSize:12,fontWeight:600,zIndex:20,display:"flex",gap:8,alignItems:"center",flexWrap:"wrap",maxWidth:"90%"}}>
          {editingFibra && <span style={{background:"#F59E0B",color:"#000",padding:"2px 8px",borderRadius:8,fontSize:11}}>Editando</span>}
          <span>{fibraPoints.length===0?"Toca dispositivo o mapa para iniciar":fibraPoints.length+" puntos"}</span>
          {fibraPoints.length>0 && <span onClick={undoPoint} style={{background:"rgba(255,255,255,0.3)",padding:"3px 8px",borderRadius:10,cursor:"pointer",fontSize:11}}>Deshacer</span>}
          {fibraPoints.length>=2 && <span onClick={finishFibra} style={{background:"#10B981",padding:"3px 10px",borderRadius:10,cursor:"pointer",fontSize:11}}>Terminar</span>}
          <span onClick={cancelFibra} style={{background:"rgba(255,255,255,0.2)",padding:"3px 8px",borderRadius:10,cursor:"pointer",fontSize:11}}>Cancelar</span>
        </div>
      )}

      <div style={{position:"absolute",bottom:70,right:10,background:"rgba(255,255,255,0.95)",borderRadius:8,padding:"8px 10px",zIndex:10,fontSize:11,boxShadow:"0 2px 6px rgba(0,0,0,0.15)"}}>
        {Object.entries(FIBRA_COLORS).map(([h,c])=>(
          <div key={h} style={{display:"flex",alignItems:"center",gap:6,marginBottom:3}}>
            <div style={{width:20,height:4,background:c,borderRadius:2}}/>
            <span>{h} hilos</span>
          </div>
        ))}
      </div>

      {/* MINI PANEL CONFIRMAR FIBRA */}
      {fibraPanel && (
        <div style={{position:"absolute",top:"50%",left:"50%",transform:"translate(-50%,-50%)",
          background:"#1a1a2e",border:"2px solid #7C3AED",borderRadius:10,padding:"16px",
          zIndex:200,minWidth:280,boxShadow:"0 4px 24px rgba(0,0,0,0.6)"}}>
          <p style={{margin:"0 0 12px",fontWeight:700,fontSize:13,color:"#C4B5FD"}}>Nueva fibra</p>
          <div style={{marginBottom:8}}>
            <label style={{fontSize:11,color:"#9CA3AF"}}>Nombre</label>
            <input value={fibraNombre} onChange={e=>setFibraNombre(e.target.value)}
              placeholder="Ej: F_TRONCAL_1"
              style={{width:"100%",background:"#0f0f1a",border:"1px solid #374151",borderRadius:6,
                color:"#F9FAFB",padding:"6px 10px",fontSize:12,marginTop:3,boxSizing:"border-box"}}/>
          </div>
          <div style={{marginBottom:12}}>
            <label style={{fontSize:11,color:"#9CA3AF"}}>Cantidad de hilos</label>
            <select value={fibraHilos} onChange={e=>setFibraHilos(e.target.value)}
              style={{width:"100%",background:"#0f0f1a",border:"1px solid #374151",borderRadius:6,
                color:"#F9FAFB",padding:"6px 10px",fontSize:12,marginTop:3}}>
              {[6,12,24,36,48,72,96,144,216,288].map(n=>(
                <option key={n} value={n}>{n} hilos</option>
              ))}
            </select>
          </div>
          <div style={{marginBottom:12}}>
            <label style={{fontSize:11,color:"#9CA3AF"}}>Número de buffers</label>
            <select value={fibraBuffers} onChange={e=>setFibraBuffers(e.target.value)}
              style={{width:"100%",background:"#0f0f1a",border:"1px solid #374151",borderRadius:6,
                color:"#F9FAFB",padding:"6px 10px",fontSize:12,marginTop:3}}>
              {[1,2,4,6,8,12].map(n=>(
                <option key={n} value={n}>{n} buffer{n>1?"s":""} ({Math.ceil(parseInt(fibraHilos)/n)} hilos c/u)</option>
              ))}
            </select>
          </div>
          {fibraPanel.eqs.length>0&&(
            <p style={{fontSize:10,color:"#6B7280",margin:"0 0 12px"}}>
              Recorrido: {fibraPanel.eqs.map(e=>(e.tipo==="olt"?"OLT":e.tipo==="mufa"?"Mufa":"NAP")+": "+(e.nombre||"")).join(" → ")}
            </p>
          )}
          <div style={{display:"flex",gap:8}}>
            <button onClick={confirmarFibra}
              style={{flex:1,background:"#7C3AED",color:"#fff",border:"none",borderRadius:6,
                padding:"8px 0",fontSize:12,cursor:"pointer",fontWeight:600}}>
              ✓ Guardar fibra
            </button>
            <button onClick={()=>{setFibraPanel(null);setFibraPoints([]);setFibraOrigen(null);setEditingFibra(null);setAddMode(null);}}
              style={{flex:1,background:"rgba(239,68,68,0.15)",color:"#F87171",border:"1px solid #F87171",
                borderRadius:6,padding:"8px 0",fontSize:12,cursor:"pointer"}}>
              ✕ Cancelar
            </button>
          </div>
        </div>
      )}

      {fibraInfo && (
        <div style={{position:"absolute",bottom:70,left:10,background:"#fff",borderRadius:10,padding:"14px",zIndex:20,maxWidth:280,boxShadow:"0 4px 16px rgba(0,0,0,0.2)"}}>
          <div style={{display:"flex",justifyContent:"space-between",marginBottom:8}}>
            <p style={{margin:0,fontWeight:700,fontSize:14}}>{fibraInfo.fibra.nombre}</p>
            <span onClick={()=>{setFibraInfo(null);setFibraSelId(null);}} style={{cursor:"pointer",color:"#999",fontSize:16}}>×</span>
          </div>
          <p style={{margin:"0 0 4px",fontSize:12,color:"#666"}}>{fibraInfo.fibra.tipo_hilos} hilos - {fibraInfo.fibra.tipo_instalacion}</p>
          <div style={{margin:"8px 0",padding:"8px",background:"#F5F7FA",borderRadius:8}}>
            <p style={{margin:"0 0 4px",fontSize:12,fontWeight:600}}>Recorrido:</p>
            {fibraInfo.eqs.length>0 ? (
              <p style={{margin:0,fontSize:12,color:"#444"}}>{fibraInfo.eqs.map(e=>tipoLabel(e.tipo)+": "+e.nombre).join(" → ")}</p>
            ) : (
              <p style={{margin:0,fontSize:12,color:"#999"}}>Sin dispositivos detectados</p>
            )}
          </div>
          <div style={{margin:"8px 0",padding:"8px",background:"#EFF6FF",borderRadius:8}}>
            <p style={{margin:"0 0 4px",fontSize:12,fontWeight:600}}>Distancia:</p>
            <p style={{margin:"0 0 2px",fontSize:12}}>Real: <strong>{fibraInfo.distReal} m</strong></p>
            <p style={{margin:"0 0 2px",fontSize:12}}>Reservas: <strong>+{fibraInfo.reserva} m</strong></p>
            <p style={{margin:0,fontSize:13,fontWeight:700,color:"#1E7BBF"}}>Total: {fibraInfo.distTotal} m</p>
          </div>
          {RC.includes(user.rol) && (
            <div style={{display:"flex",gap:8,marginTop:8}}>
              <button onClick={()=>editarFibra(fibraInfo)} style={{flex:1,padding:"6px 0",background:"#1E7BBF",color:"#fff",border:"none",borderRadius:6,fontSize:12,cursor:"pointer"}}>Editar trazado</button>
              <button onClick={()=>{ setForm({tipo:"fibra",edit:fibraInfo.fibra}); setFibraInfo(null); }} style={{flex:1,padding:"6px 0",background:"#F5F7FA",color:"#333",border:"1px solid #E8ECF0",borderRadius:6,fontSize:12,cursor:"pointer"}}>Editar datos</button>

            </div>
          )}
        </div>
      )}

      <LoadScript googleMapsApiKey={cfg.google_maps_key} libraries={LIBS}>
        <StandaloneSearchBox onLoad={ref=>setSearchBox(ref)} onPlacesChanged={onPlacesChanged}>
          <input type="text" placeholder="Buscar..." style={{boxSizing:"border-box",border:"1px solid #E8ECF0",width:"140px",height:"32px",padding:"0 10px",borderRadius:8,boxShadow:"0 2px 6px rgba(0,0,0,0.15)",fontSize:12,outline:"none",position:"absolute",top:50,left:10,zIndex:10,background:"#fff"}}/>
        </StandaloneSearchBox>
        <GoogleMap mapContainerStyle={{width:"100%",height:"100%"}} center={mapCenter} zoom={mapZoom}
          onClick={onMapClick}
          onLoad={map=>mapRef.current=map}
          onIdle={(map)=>{if(map&&map.getCenter){setMapCenter({lat:map.getCenter().lat(),lng:map.getCenter().lng()});setMapZoom(map.getZoom());}}}
          options={{streetViewControl:false,mapTypeControl:true,fullscreenControl:false}}>

          {fibraPoints.length>1 && <Polyline path={fibraPoints} options={{strokeColor:"#1E7BBF",strokeWeight:3,strokeOpacity:0.9}}/>}
          {fibraPoints.map((p,i)=>(<Marker key={"fp-"+i} position={p} icon="https://maps.google.com/mapfiles/ms/icons/ltblue-dot.png" zIndex={999}/>))}

          {(equipos.olts||[]).map(o=>(<Marker key={"olt-"+o.id} position={{lat:parseFloat(o.lat),lng:parseFloat(o.lng)}} title={o.nombre} icon={getIcon("olt",o.estado)} onClick={()=>onMarkerClick({...o,tipo:"olt"})}/>))}
          {(capa===2||capa===3)&&(equipos.mufas||[]).filter(m=>!m.poste_id).map(m=>(<Marker key={"mufa-"+m.id} position={{lat:parseFloat(m.lat),lng:parseFloat(m.lng)}} title={m.nombre} icon={getIcon("mufa",m.estado)} draggable={RC.includes(user.rol)} onDragEnd={(e)=>onDragDispositivo("mufa",m,e.latLng.lat(),e.latLng.lng())} onClick={()=>onMarkerClick({...m,tipo:"mufa"})}/>))}
          {(equipos.naps||[]).filter(n=>!n.poste_id).map(n=>(<Marker key={"nap-"+n.id} position={{lat:parseFloat(n.lat),lng:parseFloat(n.lng)}} title={n.nombre} icon={getIcon("nap",n.estado,n.puertos)} draggable={RC.includes(user.rol)} onDragEnd={(e)=>onDragDispositivo("nap",n,e.latLng.lat(),e.latLng.lng())} onClick={()=>onMarkerClick({...n,tipo:"nap"})}/>))}
          {(capa===1||capa===2)&&(equipos.postes||[])
            .filter(p=>{
              if(capa===1) return true;
              // Capa 2: solo postes sin dispositivos O postes con dispositivos (mostrar icono compuesto)
              return true; // todos, pero el icono ya muestra el dispositivo
            })
            .map(p=>{
              const {total} = getDispositivosPoste(equipos,p);
              // Capa 2: si tiene dispositivos, ocultar el poste base (el dispositivo se muestra como icono)
              if(capa===2 && total>0) return null;
              return <Marker key={"poste-"+p.id} position={{lat:parseFloat(p.lat),lng:parseFloat(p.lng)}}  title={p.nombre} icon={getIconPoste(p, capa===1)} draggable={RC.includes(user.rol)} onDragEnd={(e)=>onDragPoste(p,e.latLng.lat(),e.latLng.lng())} onClick={()=>onMarkerClick({...p,tipo:"poste"})}/>;
            })
          }
          {/* Capa 2 y 3: mostrar iconos de dispositivos en postes */}
          {(capa===2||capa===3)&&(equipos.postes||[])
            .filter(p=>getDispositivosPoste(equipos,p).total>0)
            .map(p=>{
              const {mufas:pm, naps:pn, total:pt} = getDispositivosPoste(equipos,p);
              if(pt===0) return null;
              // Si hay un solo dispositivo usar el mismo icono estandar
              let iconUrl;
              if(pt===1 && pm.length===1) iconUrl = SVG_MUFA;
              else if(pt===1 && pn.length===1) iconUrl = parseInt(pn[0].puertos)>=16 ? SVG_NAP16 : SVG_NAP8;
              else {
                // Multiples: combinar iconos en SVG horizontal
                const items = [...pm.map(()=>({tipo:"mufa"})), ...pn.map(n=>({tipo:"nap",puertos:n.puertos}))];
                const iw=36, gap=4, totalW=items.length*(iw+gap)-gap;
                let svgItems='';
                items.forEach((it,i)=>{
                  const x=i*(iw+gap);
                  if(it.tipo==="mufa") svgItems+=`<circle cx="${x+18}" cy="18" r="17" fill="#FBBF24" stroke="#92400E" stroke-width="1.5"/><circle cx="${x+18}" cy="18" r="4" fill="#111"/><path d="M${x+18},14 L${x+18},5 A4,4 0 0,1 ${x+21.5},7 Z" fill="#111"/><path d="M${x+18},14 L${x+18},5 A4,4 0 0,0 ${x+14.5},7 Z" fill="#111"/><path d="M${x+18},22 L${x+18},31 A4,4 0 0,1 ${x+14.5},29 Z" fill="#111"/><path d="M${x+18},22 L${x+18},31 A4,4 0 0,0 ${x+21.5},29 Z" fill="#111"/><path d="M${x+22},18 L${x+31},18 A4,4 0 0,1 ${x+29},21.5 Z" fill="#111"/><path d="M${x+22},18 L${x+31},18 A4,4 0 0,0 ${x+29},14.5 Z" fill="#111"/><path d="M${x+14},18 L${x+5},18 A4,4 0 0,1 ${x+7},14.5 Z" fill="#111"/><path d="M${x+14},18 L${x+5},18 A4,4 0 0,0 ${x+7},21.5 Z" fill="#111"/><circle cx="${x+18}" cy="18" r="2" fill="#FBBF24"/>`;
                  else { const col=parseInt(it.puertos)>=16?"#2563EB":"#16A34A"; const brd=parseInt(it.puertos)>=16?"#1E3A8A":"#14532D"; svgItems+=`<polygon points="${x+18},34 ${x+2},2 ${x+34},2" fill="${col}" stroke="${brd}" stroke-width="1.5" stroke-linejoin="round"/>`; }
                });
                iconUrl=`data:image/svg+xml,${encodeURIComponent(`<svg xmlns="http://www.w3.org/2000/svg" width="${totalW}" height="20" viewBox="0 0 ${totalW} 36">${svgItems}</svg>`)}`;
              }
              return <Marker key={"disp-"+p.id} position={{lat:parseFloat(p.lat),lng:parseFloat(p.lng)}} title={p.nombre} icon={iconUrl} onClick={()=>onMarkerClick({...p,tipo:"poste"})}/>;
            })
          }

          {lines.map(l=>(
            <React.Fragment key={"f-"+l.id}>
              {fibraSelId===l.id && editingFibraId!==l.id && <Polyline path={l.path} options={{strokeColor:"#FFFFFF",strokeWeight:10,strokeOpacity:0.4}}/>}
              <Polyline key={"poly-"+l.id+"-"+(editingFibraId===l.id?"e":fibraVersion)} path={l.path}
                onLoad={ref=>{ polylineRefs.current[l.id]=ref; }}
                options={{strokeColor:editingFibraId===l.id?"#00FF00":fibraSelId===l.id?"#FFFF00":l.color, strokeWeight:editingFibraId===l.id?5:fibraSelId===l.id?6:4, strokeOpacity:0.9, zIndex:editingFibraId===l.id?9999:fibraSelId===l.id?999:1, editable:editingFibraId===l.id}}
                onClick={()=>onFibraClick(l.fibra)}/>
            </React.Fragment>
          ))}

          {!modoFibra && sel && (
            <InfoWindow position={{lat:parseFloat(sel.lat),lng:parseFloat(sel.lng)}} onCloseClick={()=>setSel(null)}>
              <div style={{minWidth:190,fontFamily:"Roboto,sans-serif"}}>
                <p style={{margin:"0 0 2px",fontWeight:700,fontSize:14}}>{sel.nombre}</p>

                {/* CAPA 1 - opciones de poste */}
                {sel.tipo==="poste"&&capa===1&&<>
                  <p style={{margin:"0 0 4px",fontSize:11,color:"#666"}}>{sel.tipo_energia||""} · {sel.material||""} · {sel.altura_metros||"-"}m</p>
                  <div style={{display:"flex",gap:4,flexWrap:"wrap",marginTop:6}}>
                    {RC.includes(user.rol)&&<button onClick={()=>setForm({tipo:"poste",edit:sel})} style={{padding:"4px 8px",background:"#F59E0B",color:"#fff",border:"none",borderRadius:4,fontSize:11,cursor:"pointer"}}>⚙️ Editar</button>}
                    {RF.includes(user.rol)&&<button onClick={()=>setForm({tipo:"mufa",lat:sel.lat,lng:sel.lng,poste_id:sel.id})} style={{padding:"4px 8px",background:"#2563EB",color:"#fff",border:"none",borderRadius:4,fontSize:11,cursor:"pointer"}}>+ Mufa</button>}
                    {RF.includes(user.rol)&&<button onClick={()=>setForm({tipo:"nap",lat:sel.lat,lng:sel.lng,poste_id:sel.id})} style={{padding:"4px 8px",background:"#16A34A",color:"#fff",border:"none",borderRadius:4,fontSize:11,cursor:"pointer"}}>+ NAP</button>}
                    {RC.includes(user.rol)&&<button onClick={()=>delEq("poste",sel.id)} style={{padding:"4px 8px",background:"#FEE2E2",color:"#991B1B",border:"none",borderRadius:4,fontSize:11,cursor:"pointer"}}>Eliminar</button>}
                  </div>
                </>}

                {/* CAPA 2 y 3 - poste sin dispositivos */}
                {sel.tipo==="poste"&&(capa===2||capa===3)&&getDispositivosPoste(equipos,sel).total===0&&<>
                  <p style={{margin:"0 0 4px",fontSize:11,color:"#666"}}>Sin dispositivos</p>
                  <div style={{display:"flex",gap:4,flexWrap:"wrap",marginTop:6}}>
                    {RF.includes(user.rol)&&<button onClick={()=>setForm({tipo:"mufa",lat:sel.lat,lng:sel.lng,poste_id:sel.id})} style={{padding:"4px 8px",background:"#2563EB",color:"#fff",border:"none",borderRadius:4,fontSize:11,cursor:"pointer"}}>+ Mufa</button>}
                    {RF.includes(user.rol)&&<button onClick={()=>setForm({tipo:"nap",lat:sel.lat,lng:sel.lng,poste_id:sel.id})} style={{padding:"4px 8px",background:"#16A34A",color:"#fff",border:"none",borderRadius:4,fontSize:11,cursor:"pointer"}}>+ NAP</button>}
                  </div>
                </>}

                {/* CAPA 2 y 3 - poste con dispositivos */}
                {sel.tipo==="poste"&&(capa===2||capa===3)&&getDispositivosPoste(equipos,sel).total>0&&<>
                  {(equipos.mufas||[]).filter(m=>String(m.poste_id)===String(sel.id)).map(m=>(
                    <div key={"m"+m.id} style={{borderTop:"1px solid #eee",marginTop:4,paddingTop:4}}>
                      <p style={{margin:"0 0 3px",fontSize:12,fontWeight:600}}>☢ {m.nombre}</p>
                      <div style={{display:"flex",gap:3}}>
                        {RF.includes(user.rol)&&<button onClick={()=>onVerDiagramaMufa(m)} style={{padding:"3px 7px",background:"#6D28D9",color:"#fff",border:"none",borderRadius:4,fontSize:10,cursor:"pointer"}}>Diagrama</button>}
                        {RC.includes(user.rol)&&<button onClick={()=>setForm({tipo:"mufa",edit:m})} style={{padding:"3px 7px",background:"#1E7BBF",color:"#fff",border:"none",borderRadius:4,fontSize:10,cursor:"pointer"}}>Editar</button>}
                        {RC.includes(user.rol)&&<button onClick={()=>delEq("mufa",m.id)} style={{padding:"3px 7px",background:"#FEE2E2",color:"#991B1B",border:"none",borderRadius:4,fontSize:10,cursor:"pointer"}}>X</button>}
                      </div>
                    </div>
                  ))}
                  {(equipos.naps||[]).filter(n=>String(n.poste_id)===String(sel.id)).map(n=>(
                    <div key={"n"+n.id} style={{borderTop:"1px solid #eee",marginTop:4,paddingTop:4}}>
                      <p style={{margin:"0 0 3px",fontSize:12,fontWeight:600}}>▼ {n.nombre} ({n.puertos}p)</p>
                      <div style={{display:"flex",gap:3}}>
                        {RF.includes(user.rol)&&<button onClick={()=>onVerDiagrama(n)} style={{padding:"3px 7px",background:"#7C3AED",color:"#fff",border:"none",borderRadius:4,fontSize:10,cursor:"pointer"}}>Diagrama</button>}
                        {RC.includes(user.rol)&&<button onClick={()=>setForm({tipo:"nap",edit:n})} style={{padding:"3px 7px",background:"#1E7BBF",color:"#fff",border:"none",borderRadius:4,fontSize:10,cursor:"pointer"}}>Editar</button>}
                        {RC.includes(user.rol)&&<button onClick={()=>delEq("nap",n.id)} style={{padding:"3px 7px",background:"#FEE2E2",color:"#991B1B",border:"none",borderRadius:4,fontSize:10,cursor:"pointer"}}>X</button>}
                      </div>
                    </div>
                  ))}
                  <div style={{marginTop:6,display:"flex",gap:3}}>
                    {RF.includes(user.rol)&&<button onClick={()=>setForm({tipo:"mufa",lat:sel.lat,lng:sel.lng,poste_id:sel.id})} style={{padding:"3px 7px",background:"#2563EB",color:"#fff",border:"none",borderRadius:4,fontSize:10,cursor:"pointer"}}>+ Mufa</button>}
                    {RF.includes(user.rol)&&<button onClick={()=>setForm({tipo:"nap",lat:sel.lat,lng:sel.lng,poste_id:sel.id})} style={{padding:"3px 7px",background:"#16A34A",color:"#fff",border:"none",borderRadius:4,fontSize:10,cursor:"pointer"}}>+ NAP</button>}
                  </div>
                </>}

                {/* OLT, Mufa, NAP sin poste */}
                {sel.tipo!=="poste"&&<>
                  <p style={{margin:"0 0 4px",fontSize:12,color:"#666"}}>{(sel.tipo||"").toUpperCase()}</p>
                  {sel.tipo==="nap"&&<p style={{margin:"0 0 6px",fontSize:12}}>Puertos: {sel.puertos_ocupados||0}/{sel.puertos} - {sel.tipo_splitter}</p>}
                  {clientes.length>0&&sel.tipo==="nap"&&clientes.map(c=>(<p key={c.id} style={{margin:"0 0 2px",fontSize:11}}>Puerto {c.puerto}: {c.nombre}</p>))}
                  <div style={{display:"flex",gap:4,flexWrap:"wrap",marginTop:6}}>
                    {RF.includes(user.rol)&&sel.tipo==="nap"&&<button onClick={()=>setForm({tipo:"cliente",nap:sel})} style={{padding:"4px 8px",background:"#10B981",color:"#fff",border:"none",borderRadius:4,fontSize:11,cursor:"pointer"}}>+ Cliente</button>}
                    {RF.includes(user.rol)&&sel.tipo==="nap"&&<button onClick={()=>onVerDiagrama(sel)} style={{padding:"4px 8px",background:"#7C3AED",color:"#fff",border:"none",borderRadius:4,fontSize:11,cursor:"pointer"}}>Diagrama</button>}
                    {RF.includes(user.rol)&&sel.tipo==="mufa"&&<button onClick={()=>onVerDiagramaMufa(sel)} style={{padding:"4px 8px",background:"#6D28D9",color:"#fff",border:"none",borderRadius:4,fontSize:11,cursor:"pointer"}}>Diagrama</button>}
                    {RF.includes(user.rol)&&sel.tipo==="olt"&&<button onClick={()=>onVerDiagramaOlt(sel)} style={{padding:"4px 8px",background:"#0891B2",color:"#fff",border:"none",borderRadius:4,fontSize:11,cursor:"pointer"}}>Diagrama</button>}
                    {RC.includes(user.rol)&&<button onClick={()=>setForm({tipo:sel.tipo,edit:sel})} style={{padding:"4px 8px",background:"#1E7BBF",color:"#fff",border:"none",borderRadius:4,fontSize:11,cursor:"pointer"}}>Editar</button>}
                    {RC.includes(user.rol)&&<button onClick={()=>delEq(sel.tipo,sel.id)} style={{padding:"4px 8px",background:"#FEE2E2",color:"#991B1B",border:"none",borderRadius:4,fontSize:11,cursor:"pointer"}}>Eliminar</button>}
                    {RF.includes(user.rol)&&<button onClick={()=>setForm({tipo:"averia",equipo:sel})} style={{padding:"4px 8px",background:"#EF4444",color:"#fff",border:"none",borderRadius:4,fontSize:11,cursor:"pointer"}}>Averia</button>}
                  </div>
                </>}
              </div>
            </InfoWindow>
          )}
        </GoogleMap>
      </LoadScript>
    </div>
  );
}
